import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { User } from '@supabase/supabase-js';

interface AdminUser {
  id: string;
  userId: string;
  role: 'admin' | 'super_admin';
  createdAt: string;
}

interface AuthContextType {
  user: User | null;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithMagicLink: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAdmin = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [adminData, setAdminData] = useState<AdminUser | null>(null);
  const [loading, setLoadingState] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Use a local wrapper to keep loading updates centralized.
  const setLoading = (value: boolean) => {
    setLoadingState(value);
  };

  const checkAdminStatus = async (userId: string) => {
    try {
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Admin status check timed out')), 5000)
      );

      const queryPromise = supabase
        .from('admin_users')
        .select('*')
        .eq('user_id', userId)
        .single();

      const { data, error } = await Promise.race([queryPromise, timeoutPromise]);

      if (error && error.code !== 'PGRST116') {
        console.error('Error checking admin status:', error);
        return;
      }

      setAdminData(
        data
          ? {
              id: data.id,
              userId: data.user_id,
              role: data.role,
              createdAt: data.created_at,
            }
          : null,
      );
    } catch (err: any) {
      console.error('Error checking admin status:', err.message);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const initSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (!isMounted) return;

        if (session?.user) {
          setUser(session.user);
          await checkAdminStatus(session.user.id);
        } else {
          setUser(null);
          setAdminData(null);
        }
        setLoading(false);
      } catch (err) {
        console.error('AuthProvider: getSession error:', err);
        if (isMounted) setLoading(false);
      }
    };

    initSession();

    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        if (!isMounted) return;

        if (session?.user) {
          setUser(session.user);
          await checkAdminStatus(session.user.id);
        } else {
          setUser(null);
          setAdminData(null);
        }
        setLoading(false);
        setError(null);
      },
    );

    return () => {
      isMounted = false;
      authListener?.subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    setError(null);
    setLoading(true);

    try {
      const authStatePromise = new Promise<void>((resolve, reject) => {
        let subscription: { unsubscribe: () => void } | null = null;

        const timeout = setTimeout(() => {
          subscription?.unsubscribe();
          reject(new Error('Sign in timeout. Please try again.'));
        }, 10000);

        const cleanup = (err?: Error) => {
          clearTimeout(timeout);
          subscription?.unsubscribe();
          if (err) reject(err);
        };

        const { data } = supabase.auth.onAuthStateChange((event) => {
          if (event === 'SIGNED_IN') {
            cleanup();
            resolve();
          }
        });
        subscription = data.subscription;

        supabase.auth.signInWithPassword({ email, password })
          .then(result => {
            if (result.error) {
              console.error('signInWithPassword error:', result.error.message);
              cleanup(result.error);
            }
          })
          .catch(err => {
            console.error('signInWithPassword exception:', err);
            cleanup(err);
          });
      });

      await authStatePromise;
    } catch (err: any) {
      console.error('AuthProvider: signIn error:', err.message);
      setError(err.message || 'Failed to sign in');
      setLoading(false);
      throw err;
    }
  };

  const signInWithMagicLink = async (email: string) => {
    setError(null);
    setLoading(true);

    try {
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout. Please try again.')), 10000)
      );
      const otpPromise = supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: window.location.origin,
        },
      });

      const { error } = await Promise.race([otpPromise, timeoutPromise]);
      if (error) throw error;
    } catch (err: any) {
      setError(err.message || 'Failed to send magic link');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      setUser(null);
      setAdminData(null);
      window.location.href = '/';
    } catch (err: any) {
      setError(err.message || 'Failed to sign out');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    isAdmin: !!adminData,
    isSuperAdmin: adminData?.role === 'super_admin',
    loading,
    error,
    signIn,
    signInWithMagicLink,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
